// Streams trials to the server in order, in small batches, retrying until stored.
// Sequence numbers make resends idempotent, so a dropped connection loses nothing.

import type { TrialRecord } from "./runner";

export type SendBatch = (trials: TrialRecord[]) => Promise<unknown>;

export class TrialSink {
  private queue: TrialRecord[] = [];
  private chain: Promise<void> = Promise.resolve();
  private failures = 0;
  lastError: string | null = null;
  stored = 0;

  constructor(private readonly send: SendBatch, private readonly batchSize = 5,
              private readonly onState?: (s: { pending: number; error: string | null }) => void) {}

  push(t: TrialRecord): void {
    this.queue.push(t);
    if (this.queue.length >= this.batchSize) void this.flush().catch(() => undefined);
  }

  get pending(): number {
    return this.queue.length;
  }

  /** Send everything queued; resolves when the server has stored it. Drains never overlap. */
  flush(): Promise<void> {
    this.chain = this.chain.then(() => this.drain(), () => this.drain());
    return this.chain;
  }

  private async drain(): Promise<void> {
    while (this.queue.length) {
      const batch = this.queue.slice(0, 50);
      try {
        await this.send(batch);
        this.queue.splice(0, batch.length);
        this.stored += batch.length;
        this.failures = 0;
        this.lastError = null;
      } catch (e) {
        this.failures += 1;
        this.lastError = e instanceof Error ? e.message : String(e);
        this.onState?.({ pending: this.queue.length, error: this.lastError });
        if (this.failures > 8) throw e;
        await new Promise((r) => setTimeout(r, Math.min(8000, 250 * 2 ** this.failures)));
      }
    }
    this.onState?.({ pending: 0, error: null });
  }
}
